/**
 * Patient Detail Page - Refactored version
 * Uses modular components from /components/patient
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import { ro } from 'date-fns/locale';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { ArrowLeft, Plus, FileText, Clock, Calendar, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { DatePicker } from '../components/ui/date-picker';

// Import patient components
import {
  PatientGeneralInfo,
  PatientEditDialog,
  ConsultationDialog,
  ConsultationDetailCard,
  EvolutionCharts,
  FilePreviewDialog,
  ConsentModal,
  getInitialConsultationForm
} from '../components/patient';

// Import API functions
import { 
  getPatient, 
  getPatientEvolution,
  updatePatient,
  getConsultations, 
  createConsultation,
  updateConsultation,
  getDiagnoses, 
  getMedications,
  uploadFile,
  getConsultationFiles,
  downloadFileBlob,
  deleteFile,
  deleteConsultation,
  createAppointment,
  getAvailableTimeSlots
} from '../services/api';

// Generate time slots for appointments (30 min intervals)
const generateTimeSlots = () => {
  const slots = [];
  for (let hour = 8; hour < 20; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      slots.push(`${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`);
    }
  }
  return slots;
};
const TIME_SLOTS = generateTimeSlots();

const PatientDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { theme } = useTheme();
  const printRef = useRef();
  const contentRef = useRef();
  
  // Core data state
  const [patient, setPatient] = useState(null);
  const [consultations, setConsultations] = useState([]);
  const [diagnoses, setDiagnoses] = useState([]);
  const [medications, setMedications] = useState([]);
  const [evolution, setEvolution] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Tab state
  const [activeTab, setActiveTab] = useState('info');
  
  // Dialog states
  const [consultationDialogOpen, setConsultationDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [appointmentDialogOpen, setAppointmentDialogOpen] = useState(false);
  const [consentModalOpen, setConsentModalOpen] = useState(false);
  
  // Appointment state
  const [appointmentForm, setAppointmentForm] = useState({
    appointment_date: format(new Date(), 'yyyy-MM-dd'),
    appointment_time: '09:00',
    notes: ''
  });
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  
  // Consultation state
  const [selectedConsultation, setSelectedConsultation] = useState(null);
  const [consultationFiles, setConsultationFiles] = useState([]);
  const [isEditingConsultation, setIsEditingConsultation] = useState(false);
  const [editingConsultationId, setEditingConsultationId] = useState(null);
  const [uploadingFiles, setUploadingFiles] = useState([]);
  
  // Form states
  const [editFormData, setEditFormData] = useState({});
  const [consultationForm, setConsultationForm] = useState(getInitialConsultationForm());

  // Handle tab change - scroll to top
  const handleTabChange = (value) => {
    setActiveTab(value);
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  };
  
  // File preview state
  const [previewFile, setPreviewFile] = useState(null);
  const [previewBlobUrl, setPreviewBlobUrl] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Fetch all data
  const fetchData = useCallback(async () => {
    try {
      const [patientRes, consultationsRes, diagnosesRes, medicationsRes, evolutionRes] = await Promise.all([
        getPatient(id),
        getConsultations(id),
        getDiagnoses(),
        getMedications(),
        getPatientEvolution(id)
      ]);
      
      setPatient(patientRes.data);
      setEditFormData(patientRes.data);
      setConsultations(consultationsRes.data);
      setDiagnoses(diagnosesRes.data);
      setMedications(medicationsRes.data);
      setEvolution(evolutionRes.data.evolution || []);
    } catch (error) {
      toast.error('Eroare la încărcarea datelor');
      navigate('/patients');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Deep-link from calendar: auto-open specific consultation
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    const cidParam = searchParams.get('cid');
    if (tabParam === 'consultations' && consultations.length > 0) {
      setActiveTab('consultations');
      if (cidParam) {
        const target = consultations.find(c => c.id === cidParam);
        if (target) {
          viewConsultationDetails(target);
        }
      }
    }
  }, [searchParams, consultations]);

  // Permission checks
  const canEditPatient = true; // All roles (admin, medic, asistent) can edit patient data
  const canEdit = user.role !== 'asistent'; // For other edit operations (not patient data)
  const canDelete = user.role === 'admin';

  const canEditConsultation = (consultation) => {
    // Asistent cannot edit consultations
    if (user.role === 'asistent') return false;
    // Admin and medic can edit any consultation
    return user.role === 'admin' || user.role === 'medic';
  };

  // Patient edit handlers
  const handleUpdatePatient = async (e) => {
    e.preventDefault();
    try {
      await updatePatient(id, editFormData);
      toast.success('Pacient actualizat cu succes!');
      setEditDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Eroare la actualizarea pacientului');
    }
  };

  // Appointment handlers
  const fetchAvailableSlots = async (date) => {
    setLoadingSlots(true);
    try {
      const response = await getAvailableTimeSlots(date);
      const slots = response.data.slots || response.data || [];
      setAvailableSlots(slots);
      
      // Auto-select first available slot
      const firstAvailable = slots.find(slot => slot.available);
      if (firstAvailable) {
        setAppointmentForm(prev => ({ ...prev, appointment_time: firstAvailable.time }));
      } else {
        // No slots available
        setAppointmentForm(prev => ({ ...prev, appointment_time: '' }));
      }
    } catch (error) {
      console.error('Failed to fetch slots:', error);
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleAppointmentDateChange = (date) => {
    setAppointmentForm(prev => ({ ...prev, appointment_date: date }));
    fetchAvailableSlots(date);
  };

  // Check if any slot is available
  const hasAvailableAppointmentSlots = availableSlots.some(slot => slot.available);

  const handleCreateAppointment = async (e) => {
    e.preventDefault();
    try {
      await createAppointment({
        patient_id: id,
        ...appointmentForm
      });
      toast.success('Programare creată cu succes!');
      setAppointmentDialogOpen(false);
      setAppointmentForm({
        appointment_date: format(new Date(), 'yyyy-MM-dd'),
        appointment_time: '09:00',
        notes: ''
      });
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Eroare la crearea programării';
      toast.error(errorMsg);
    }
  };

  // Consultation handlers
  const resetConsultationForm = () => {
    // Collect all unique diagnoses from all previous consultations
    const allDiagnoses = new Set();
    consultations.forEach(c => {
      (c.diagnosis_ids || []).forEach(d => allDiagnoses.add(d));
    });
    setConsultationForm(getInitialConsultationForm(
      consultations.length > 0 ? [...allDiagnoses] : []
    ));
    setUploadingFiles([]);
    setIsEditingConsultation(false);
    setEditingConsultationId(null);
  };

  const handleCreateConsultation = async (e) => {
    e.preventDefault();
    
    try {
      const consultationData = {
        ...consultationForm,
        patient_id: id,
        glycemia: consultationForm.glycemia ? parseFloat(consultationForm.glycemia) : null,
        hba1c: consultationForm.hba1c ? parseFloat(consultationForm.hba1c) : null,
        weight: consultationForm.weight ? parseFloat(consultationForm.weight) : null,
        hdl: consultationForm.hdl ? parseFloat(consultationForm.hdl) : null,
        ldl: consultationForm.ldl ? parseFloat(consultationForm.ldl) : null,
        triglycerides: consultationForm.triglycerides ? parseFloat(consultationForm.triglycerides) : null
      };
      
      let consultationId;
      if (isEditingConsultation && editingConsultationId) {
        await updateConsultation(editingConsultationId, consultationData);
        consultationId = editingConsultationId;
        // Upload new files if any
        for (const file of uploadingFiles) {
          await uploadFile(consultationId, file);
        }
        toast.success('Consultație actualizată cu succes!');
      } else {
        const response = await createConsultation(consultationData);
        consultationId = response.data.id;
        // Upload files if any
        for (const file of uploadingFiles) {
          await uploadFile(consultationId, file);
        }
        toast.success('Consultație înregistrată cu succes!');
      }
      
      setConsultationDialogOpen(false);
      resetConsultationForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la salvarea consultației');
    }
  };

  const openNewConsultation = () => {
    resetConsultationForm();
    setConsultationDialogOpen(true);
  };

  const openEditConsultation = (consultation) => {
    if (!canEditConsultation(consultation)) {
      toast.error('Nu aveți permisiunea de a edita această consultație');
      return;
    }
    
    setIsEditingConsultation(true);
    setEditingConsultationId(consultation.id);
    setConsultationForm({
      consultation_date: consultation.consultation_date,
      consultation_time: consultation.consultation_time,
      diagnosis_ids: consultation.diagnosis_ids || [],
      symptoms: consultation.symptoms || '',
      clinical_exam: consultation.clinical_exam || '',
      lab_results: consultation.lab_results || '',
      glycemia: consultation.glycemia || '',
      hba1c: consultation.hba1c || '',
      blood_pressure: consultation.blood_pressure || '',
      weight: consultation.weight || '',
      hdl: consultation.hdl || '',
      ldl: consultation.ldl || '',
      triglycerides: consultation.triglycerides || '',
      other_lab_results: consultation.other_lab_results || '',
      medications: consultation.medications || [],
      recommendations: consultation.recommendations || '',
      next_appointment: consultation.next_appointment || '',
      next_appointment_time: consultation.next_appointment_time || ''
    });
    setConsultationDialogOpen(true);
  };

  // View consultation details
  const viewConsultationDetails = async (consultation) => {
    setSelectedConsultation(consultation);
    try {
      const filesRes = await getConsultationFiles(consultation.id);
      setConsultationFiles(filesRes.data);
    } catch (error) {
      setConsultationFiles([]);
    }
  };

  const handleDeleteConsultation = async (consultationId, e) => {
    e?.stopPropagation();
    if (!window.confirm('Sigur doriți să ștergeți această consultație? Această acțiune este ireversibilă.')) {
      return;
    }
    try {
      await deleteConsultation(consultationId);
      toast.success('Consultație ștearsă cu succes!');
      setSelectedConsultation(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la ștergerea consultației');
    }
  };

  // File handlers
  const handlePreviewFile = async (file) => {
    setPreviewFile(file);
    setLoadingPreview(true);
    
    if (previewBlobUrl) {
      URL.revokeObjectURL(previewBlobUrl);
      setPreviewBlobUrl(null);
    }
    
    try {
      const blob = await downloadFileBlob(file.id);
      const blobUrl = URL.createObjectURL(blob);
      setPreviewBlobUrl(blobUrl);
    } catch (error) {
      toast.error('Eroare la încărcarea fișierului pentru preview');
      console.error('Preview error:', error);
    } finally {
      setLoadingPreview(false);
    }
  };

  const closePreview = () => {
    if (previewBlobUrl) {
      URL.revokeObjectURL(previewBlobUrl);
    }
    setPreviewFile(null);
    setPreviewBlobUrl(null);
  };

  const handleDownloadFile = async (file) => {
    try {
      const blob = await downloadFileBlob(file.id);
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = file.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      toast.error('Eroare la descărcarea fișierului');
    }
  };

  const handleDeleteFile = async (fileId) => {
    try {
      await deleteFile(fileId);
      toast.success('Fișier șters!');
      if (selectedConsultation) {
        const filesRes = await getConsultationFiles(selectedConsultation.id);
        setConsultationFiles(filesRes.data);
      }
    } catch (error) {
      toast.error('Eroare la ștergerea fișierului');
    }
  };

  // Print treatment scheme
  const printTreatmentScheme = () => {
    if (!selectedConsultation || !patient) return;
    
    const getDiagnosisName = (diagId) => {
      const diagnosis = diagnoses.find(d => d.id === diagId);
      if (diagnosis) return `${diagnosis.code}: ${diagnosis.name}`;
      return diagId;
    };
    
    const printContent = `
      <html>
        <head>
          <title>Schema de Tratament - ${patient.last_name} ${patient.first_name}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; font-size: 12px; }
            h1 { font-size: 18px; margin-bottom: 10px; text-align: center; }
            h2 { font-size: 14px; color: #666; margin-bottom: 20px; text-align: center; }
            h3 { font-size: 14px; margin-top: 25px; margin-bottom: 10px; }
            .info { margin-bottom: 20px; }
            .info p { margin: 4px 0; }
            table { width: 100%; border-collapse: collapse; margin-top: 15px; }
            th, td { border: 1px solid #333; padding: 8px; text-align: center; }
            th { background-color: #f0f0f0; font-weight: bold; }
            .med-table td { text-align: left; }
            .check { font-size: 16px; font-weight: bold; }
            .footer { margin-top: 40px; font-size: 11px; color: #666; }
            .signature { margin-top: 50px; display: flex; justify-content: space-between; }
            .recommendations { margin-top: 20px; padding: 10px; background-color: #f9f9f9; border: 1px solid #ddd; }
          </style>
        </head>
        <body>
          <h1>SCHEMA DE TRATAMENT</h1>
          <h2>DiabetoCare - Cabinet Diabetologie</h2>
          
          <div class="info">
            <p><strong>Pacient:</strong> ${patient.last_name} ${patient.first_name}</p>
            <p><strong>CNP:</strong> ${patient.cnp}</p>
            <p><strong>Data consultației:</strong> ${format(new Date(selectedConsultation.consultation_date), 'd MMMM yyyy', { locale: ro })}</p>
            <p><strong>Medic:</strong> ${selectedConsultation.doctor_name}</p>
          </div>
          
          ${selectedConsultation.diagnosis_ids?.length > 0 ? `
            <p><strong>Diagnostic:</strong> ${selectedConsultation.diagnosis_ids.map(d => getDiagnosisName(d)).join(', ')}</p>
          ` : ''}
          
          <h3>Medicație prescrisă:</h3>
          <table class="med-table">
            <thead>
              <tr>
                <th>Denumire comercială</th>
                <th>DCI</th>
                <th>Formă / Doză</th>
                <th>DS</th>
                <th>Zile</th>
                <th>☀️</th>
                <th>🌤️</th>
                <th>🌙</th>
                <th>📅</th>
              </tr>
            </thead>
            <tbody>
              ${selectedConsultation.medications?.map(med => `
                <tr>
                  <td>${med.commercial_name || '-'}</td>
                  <td>${med.inn_name || '-'}</td>
                  <td>${med.form || '-'} - ${med.dose || '-'}</td>
                  <td>${med.ds || '-'}</td>
                  <td style="text-align: center;">${med.treatment_days || '-'}</td>
                  <td><span class="check">${med.morning ? '✓' : ''}</span></td>
                  <td><span class="check">${med.lunch ? '✓' : ''}</span></td>
                  <td><span class="check">${med.evening ? '✓' : ''}</span></td>
                  <td><span class="check">${med.once_weekly ? '✓' : ''}</span></td>
                </tr>
              `).join('') || '<tr><td colspan="9">Nu sunt medicamente prescrise</td></tr>'}
            </tbody>
          </table>
          
          ${selectedConsultation.recommendations ? `
            <div class="recommendations">
              <h3>Recomandări:</h3>
              <p>${selectedConsultation.recommendations}</p>
            </div>
          ` : ''}
          
          ${selectedConsultation.next_appointment ? `
            <p style="margin-top: 20px;"><strong>Programare control:</strong> ${format(new Date(selectedConsultation.next_appointment), 'd MMMM yyyy', { locale: ro })}${selectedConsultation.next_appointment_time ? ` la ora ${selectedConsultation.next_appointment_time}` : ''}</p>
          ` : ''}
          
          <div class="signature">
            <div>
              <p>Semnătură medic:</p>
              <p>_________________________</p>
            </div>
            <div>
              <p>Semnătură pacient:</p>
              <p>_________________________</p>
            </div>
          </div>
          
          <div class="footer">
            <p>Document generat din DiabetoCare la ${format(new Date(), 'd MMMM yyyy HH:mm', { locale: ro })}</p>
          </div>
        </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  // Helper function
  const getDiagnosisName = (diagId) => {
    const diagnosis = diagnoses.find(d => d.id === diagId);
    if (diagnosis) return `${diagnosis.code}: ${diagnosis.name}`;
    return diagId;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-500">Se încarcă datele pacientului...</p>
        </div>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Pacientul nu a fost găsit.</p>
        <Button variant="outline" onClick={() => navigate('/patients')} className="mt-4">
          Înapoi la pacienți
        </Button>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-120px)] flex flex-col" data-testid="patient-detail-page">
      {/* Sticky Header */}
      <div className="flex-shrink-0 bg-white pb-4 border-b border-slate-100 mb-4">
        {/* Top row - Back button, patient name, action buttons */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/patients')}
              data-testid="back-to-patients"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Înapoi
            </Button>
            <div>
              <h1 className="text-2xl font-bold">{patient.last_name} {patient.first_name}</h1>
              <p className="text-slate-500">CNP: {patient.cnp}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {canEditPatient && (
              <Button 
                onClick={() => setEditDialogOpen(true)}
                variant="outline"
                className={cn("border-2", theme.borderColor, theme.textColor, theme.primaryBgLightHover)}
                data-testid="edit-patient-header-btn"
              >
                <FileText className="w-4 h-4 mr-2" /> Editează datele
              </Button>
            )}
            {!patient.is_placeholder && (
              <Button
                onClick={() => setConsentModalOpen(true)}
                variant="outline"
                className={cn(
                  "border-2 hidden md:flex",
                  patient.signature
                    ? "border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                    : "border-red-500 text-red-700 hover:bg-red-50"
                )}
                data-testid="consent-gdpr-btn"
              >
                <ShieldCheck className="w-4 h-4 mr-2" />
                Consimțământ GDPR
              </Button>
            )}
            {canEdit && (
              <Button 
                onClick={openNewConsultation} 
                className={cn(theme.primaryBg, theme.primaryBgHover)}
                data-testid="new-consultation-btn"
              >
                <Plus className="w-4 h-4 mr-2" /> Consultație nouă
              </Button>
            )}
            <Button 
              onClick={() => {
                setAppointmentDialogOpen(true);
                fetchAvailableSlots(appointmentForm.appointment_date);
              }}
              className={cn(theme.primaryBg, theme.primaryBgHover)}
              data-testid="new-appointment-from-patient-btn"
            >
              <Calendar className="w-4 h-4 mr-2" /> Programare Nouă
            </Button>
          </div>
        </div>

        {/* Tabs - larger, more visible, themed */}
        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          <TabsList className="bg-slate-100 p-1 h-auto w-full justify-start">
            <TabsTrigger 
              value="info" 
              data-testid="tab-info"
              className="text-base font-semibold px-6 py-3 rounded-lg transition-all data-[state=active]:bg-white data-[state=active]:shadow-sm"
              style={{ '--theme-border': theme.primaryHex }}
            >
              Informații Generale
            </TabsTrigger>
            <TabsTrigger 
              value="consultations" 
              data-testid="tab-consultations"
              className="text-base font-semibold px-6 py-3 rounded-lg transition-all data-[state=active]:bg-white data-[state=active]:shadow-sm"
              style={{ '--theme-border': theme.primaryHex }}
            >
              Consultații ({consultations.length})
            </TabsTrigger>
            <TabsTrigger 
              value="evolution" 
              data-testid="tab-evolution"
              className="text-base font-semibold px-6 py-3 rounded-lg transition-all data-[state=active]:bg-white data-[state=active]:shadow-sm"
              style={{ '--theme-border': theme.primaryHex }}
            >
              Evoluție
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto" ref={contentRef}>
        <Tabs value={activeTab} className="h-full">
          {/* Info Tab - Full width patient info */}
          <TabsContent value="info" className="mt-0">
            <PatientGeneralInfo
              patient={patient}
              consultations={consultations}
              diagnoses={diagnoses}
              canEdit={false}
              onEditClick={() => {}}
            />
          </TabsContent>

        {/* Consultations Tab */}
        <TabsContent value="consultations" className="mt-0">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Consultations list */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <FileText className="w-5 h-5" />
                  Selectează o consultație
                </CardTitle>
              </CardHeader>
              <CardContent>
                {consultations.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">
                    Nu există consultații înregistrate pentru acest pacient.
                  </p>
                ) : (
                  <div className="space-y-3 max-h-[600px] overflow-y-auto">
                    {consultations.map((consultation) => (
                      <div
                        key={consultation.id}
                        className={cn(
                          "p-4 rounded-lg cursor-pointer transition-all",
                          selectedConsultation?.id === consultation.id
                            ? 'bg-white shadow-md'
                            : 'border border-slate-200 hover:bg-slate-50'
                        )}
                        style={selectedConsultation?.id === consultation.id 
                          ? { border: `2px solid ${theme.primaryHex}` }
                          : {}
                        }
                        onClick={() => viewConsultationDetails(consultation)}
                        data-testid={`consultation-row-${consultation.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium">
                            {format(new Date(consultation.consultation_date), 'd MMMM yyyy', { locale: ro })}
                          </span>
                          <span className="text-sm text-slate-500">
                            {consultation.consultation_time}
                          </span>
                        </div>
                        <p className="text-sm text-slate-600 mt-1">Dr. {consultation.doctor_name}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Selected consultation details */}
            {selectedConsultation && (
              <ConsultationDetailCard
                consultation={selectedConsultation}
                files={consultationFiles}
                diagnoses={diagnoses}
                canEdit={canEditConsultation(selectedConsultation)}
                canDelete={canDelete}
                onEdit={() => openEditConsultation(selectedConsultation)}
                onDelete={(e) => handleDeleteConsultation(selectedConsultation.id, e)}
                onPrint={printTreatmentScheme}
                onClose={() => setSelectedConsultation(null)}
                onPreviewFile={handlePreviewFile}
                onDownloadFile={handleDownloadFile}
                onDeleteFile={handleDeleteFile}
                theme={theme}
              />
            )}
          </div>
        </TabsContent>

        {/* Evolution Tab */}
        <TabsContent value="evolution" className="mt-0">
          <EvolutionCharts evolution={evolution} />
        </TabsContent>
        </Tabs>
      </div>

      {/* Dialogs */}
      <PatientEditDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        formData={editFormData}
        setFormData={setEditFormData}
        onSubmit={handleUpdatePatient}
        currentPatientId={id}
      />

      <ConsultationDialog
        open={consultationDialogOpen}
        onOpenChange={setConsultationDialogOpen}
        isEditing={isEditingConsultation}
        consultationForm={consultationForm}
        setConsultationForm={setConsultationForm}
        diagnoses={diagnoses}
        medications={medications}
        uploadingFiles={uploadingFiles}
        setUploadingFiles={setUploadingFiles}
        onSubmit={handleCreateConsultation}
      />

      <FilePreviewDialog
        file={previewFile}
        blobUrl={previewBlobUrl}
        loading={loadingPreview}
        onClose={closePreview}
        onDownload={handleDownloadFile}
      />

      {/* Appointment Dialog */}
      <Dialog open={appointmentDialogOpen} onOpenChange={setAppointmentDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Programare Nouă</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateAppointment} className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Pacient</Label>
              <Input 
                value={`${patient?.last_name} ${patient?.first_name}`}
                disabled
                className="bg-slate-50"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Data *</Label>
                <DatePicker
                  value={appointmentForm.appointment_date}
                  onChange={(date) => handleAppointmentDateChange(date)}
                  minDate={format(new Date(), 'yyyy-MM-dd')}
                  placeholder="Selectați data"
                />
              </div>
              <div className="space-y-2">
                <Label>Ora *</Label>
                <Select 
                  value={appointmentForm.appointment_time}
                  onValueChange={(value) => setAppointmentForm(prev => ({ ...prev, appointment_time: value }))}
                >
                  <SelectTrigger disabled={!hasAvailableAppointmentSlots && !loadingSlots}>
                    <SelectValue placeholder={!hasAvailableAppointmentSlots && !loadingSlots ? "Nu există sloturi disponibile" : "Selectați ora"} />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {loadingSlots ? (
                      <div className="p-2 text-sm text-slate-500">Se încarcă...</div>
                    ) : availableSlots.length > 0 ? (
                      availableSlots.map((slot) => (
                        <SelectItem 
                          key={slot.time} 
                          value={slot.time}
                          disabled={!slot.available}
                          className={!slot.available ? 'text-red-400 line-through' : ''}
                        >
                          {slot.time} {!slot.available && '(ocupat)'}
                        </SelectItem>
                      ))
                    ) : (
                      TIME_SLOTS.map((time) => (
                        <SelectItem key={time} value={time}>{time}</SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                {!hasAvailableAppointmentSlots && !loadingSlots && availableSlots.length > 0 && (
                  <p className="text-xs text-red-500">Nu mai există sloturi disponibile pentru această zi</p>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notițe</Label>
              <Textarea
                value={appointmentForm.notes}
                onChange={(e) => setAppointmentForm(prev => ({ ...prev, notes: e.target.value }))}
                rows={2}
                placeholder="Observații pentru programare..."
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setAppointmentDialogOpen(false)}>
                Anulează
              </Button>
              <Button 
                type="submit" 
                className={cn(theme.primaryBg, theme.primaryBgHover)}
                disabled={!hasAvailableAppointmentSlots || !appointmentForm.appointment_time}
              >
                Programează
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <ConsentModal
        open={consentModalOpen}
        onOpenChange={setConsentModalOpen}
        patient={patient}
      />
    </div>
  );
};

export default PatientDetailPage;
